import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { db } from '../data/store';
import {
  collection, doc, setDoc, deleteDoc, updateDoc, getDoc, getDocs, onSnapshot, query, orderBy, where, newId, writeBatch } from '@battleops/localstore';
import { loadIdentity } from '../session/session';
import { makeTimerState, bankable, applyWrites, localDateStr, localToday, bySort, planTaskDrop, planBucketTasks, commands, ownSessions, moveTasksToDepartment as moveTasksToDepartment_, sessionDepartmentStamp, stampEntryDepartment, bucketDepartmentId, newTaskDepartmentId, inferTaskDepartment, deptGuessStamp, captureDefaults, doneRules, automationRunsBucket, GENERAL_DEPARTMENT_ID, isVoidTask, travelIdleEnd } from '@battleops/shared';
import { useDeptLearn } from '../data/deptLearn';
import { useLocalCollection } from '../data/useLocal';
import { atomicCompleteTask, atomicStopTimer } from '../data/atomic';
import { commitWrites } from '../data/commit';
import { getSync, currentUid } from '../session/boot';
import { platform } from '../platform';
import { logTaskTransition } from '../data/taskFlowLog';
import { useDepartments } from './DepartmentContext';
import { useProjects } from './ProjectContext';
import { makeTaskDoc } from '../data/taskDoc';
import { fireProcessEvent } from '../data/processRun';
// Photos / voice notes on a task stay on the device until uploaded (phone: mediaQueue). No-op where
// the platform has no media queue.
// The phone keeps a durable copy of any local photo, queues its upload and keeps the shared photo index in step (before → after).
const keepMedia = (id, before, after) => platform().media?.keepTaskMedia?.(id, before, after);

const TaskContext = createContext();
const TASKS_COLLECTION = 'tasks';
const META_COLLECTION = 'meta';
const SESSIONS_COLLECTION = 'clockSessions';

// meta/timer and meta/lock are the SINGLE SOURCE OF TRUTH for timer/lock state.
// The reducer stores the raw docs; everything (currentTaskStart, pauseStatus,
// lockedTaskId, activeTaskId) is DERIVED from them in the provider body so no two
// places can diverge.
const initialState = {
  tasks: [],
  voidTasks: [],
  clockSessions: [],
  allClockSessions: [],
  clockedIn: null,
  loaded: false,
  timer: null, // raw meta/timer doc: { taskId, startedAt, pauseType, pauseStartedAt, pauseContext, accumulatedPause }
  lock: null, // raw meta/lock doc: { active, taskId }
  timerRestored: false, // true once we've read meta/timer at least once
};

// Void tasks (closed with their project, shared/projectclose.js) never enter `tasks`: every list, plan, board, count
// and command reads `tasks` and must not see them. They live apart in `voidTasks` for the views that need them —
// the time views, the Daily Log, the task page, the Tag page (`tasksWithVoid`).
const splitVoid = (list) => (list.some(isVoidTask) ? [list.filter((t) => !isVoidTask(t)), list.filter(isVoidTask)] : [list, []]);

function taskReducer(state, action) {
  switch (action.type) {
    case 'LOAD_TASKS': {
      const [tasks, voidTasks] = splitVoid(action.tasks);
      return { ...state, tasks, voidTasks, loaded: true };
    }
    case 'LOAD_SESSIONS':
      return { ...state, clockSessions: action.sessions, allClockSessions: action.all || action.sessions };
    case 'SET_CLOCK':
      return { ...state, clockedIn: action.clock };
    case 'SET_TASKS': {   // an optimistic list: a task voided in it moves to voidTasks, a restored one leaves it
      const [tasks, nowVoid] = splitVoid(action.tasks);
      const ids = new Set(action.tasks.map((t) => t.id));
      const kept = (state.voidTasks || []).filter((t) => !ids.has(t.id));
      return { ...state, tasks, voidTasks: nowVoid.length || kept.length !== (state.voidTasks || []).length ? [...kept, ...nowVoid] : state.voidTasks };
    }
    case 'SET_TIMER':
      return { ...state, timer: action.timer, timerRestored: true };
    case 'SET_LOCK':
      return { ...state, lock: action.lock };
    case 'CLOCK_IN':
      return { ...state, clockedIn: { startedAt: Date.now(), projectId: action.projectId || null, sessionId: action.sessionId || null, mode: action.mode || 'work' } };
    case 'CLOCK_OUT':
      return { ...state, clockedIn: null };
    case 'SWITCH_PROJECT':
      return { ...state, clockedIn: state.clockedIn ? { ...state.clockedIn, projectId: action.projectId } : null };
    case 'COMPLETE_TASK': {
      // Optimistic: hide the card immediately. Real time accumulation comes from
      // the atomic function via the tasks snapshot.
      return {
        ...state,
        tasks: state.tasks.map(t =>
          t.id === action.id ? { ...t, status: 'done', completedAt: Date.now() } : t
        ),
      };
    }
    case 'SKIP_TASK': {
      // Optimistic skip metadata only.
      return {
        ...state,
        tasks: state.tasks.map(t =>
          t.id === action.id
            ? { ...t, skippedCount: (t.skippedCount || 0) + 1, lastSkippedAt: Date.now() }
            : t
        ),
      };
    }
    case 'RESET_TASK':
      return {
        ...state,
        tasks: state.tasks.map(t =>
          t.id === action.id ? { ...t, status: 'todo', completedAt: null, completion: null } : t
        ),
      };
    default:
      return state;
  }
}

// Firestore helpers
async function saveTaskToFirestore(task) {
  try {
    await setDoc(doc(db, TASKS_COLLECTION, task.id), task);
  } catch (e) {
    console.error('Firestore save failed:', e);
  }
}

// Use updateDoc for partial updates to avoid overwriting concurrent changes
async function updateTaskInFirestore(id, updates) {
  try {
    await updateDoc(doc(db, TASKS_COLLECTION, id), updates);
  } catch (e) {
    console.error('Firestore update failed:', e);
  }
}

async function deleteTaskFromFirestore(id) {
  try {
    await deleteDoc(doc(db, TASKS_COLLECTION, id));
  } catch (e) {
    console.error('Firestore delete failed:', e);
  }
}

// Rebuild the in-memory clock from its Firestore doc. MUST carry the travel-overlay fields
// (traveling/travelStartedAt/travelSessionId) — otherwise a snapshot echo right after
// startTravel strips the overlay and the travel timer flickers off ~half a second later.
function hydrateClock(data) {
  if (!data || !data.active) return null;
  return {
    startedAt: data.startedAt,
    projectId: data.projectId || null,
    sessionId: data.sessionId || null,
    mode: data.mode || 'work',
    ...(data.traveling ? { traveling: true, travelStartedAt: data.travelStartedAt, travelSessionId: data.travelSessionId, ...(data.travelAnchor ? { travelAnchor: data.travelAnchor } : {}), ...(data.travelMovedAt ? { travelMovedAt: data.travelMovedAt } : {}) } : {}),
  };
}

async function saveClockState(clock) {
  try {
    await setDoc(doc(db, META_COLLECTION, 'clock'), clock
      ? { ...clock, active: true }
      : { active: false }
    );
  } catch (e) {
    console.error('Firestore clock save failed:', e);
  }
}

// Work sessions carry their project's department (reports group by it without a lookup) and
// feedBooks:false when that department's time must not become a BattleBooks timesheet entry.
// Set by the provider on every render, so it always sees the current departments.
let sessionStamp = () => ({});
function stampSession(session) {
  if (!session || session.type === 'travel') return session;
  return { ...sessionStamp(session.projectId || null, session.currentTaskId || null), ...session };
}

async function saveClockSession(session) {
  try {
    await setDoc(doc(db, SESSIONS_COLLECTION, session.id), stampSession(session));
  } catch (e) {
    console.error('Firestore session save failed:', e);
  }
}

async function saveTimerState(timer) {
  try {
    await setDoc(doc(db, META_COLLECTION, 'timer'), timer);
  } catch (e) {
    console.error('Firestore timer save failed:', e);
  }
}

function clearTimerState() {
  saveTimerState(makeTimerState());
}

async function unblockDependentTasks(completedTaskId) {
  try {
    const q = query(collection(db, TASKS_COLLECTION), where('blockedByTaskId', '==', completedTaskId));
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d =>
      updateDoc(doc(db, TASKS_COLLECTION, d.id), { status: 'todo', blockedByTaskId: null })
    ));
  } catch (e) {
    console.error('Failed to unblock dependent tasks:', e);
  }
}

async function checkSectionCompletion(sectionId, projectId) {
  if (!sectionId || !projectId) return;
  try {
    const taskQ = query(collection(db, TASKS_COLLECTION), where('sectionId', '==', sectionId));
    const taskSnap = await getDocs(taskQ);
    const sectionTasks = taskSnap.docs.map(d => d.data()).filter(t => !t.parentId);
    if (sectionTasks.length === 0 || !sectionTasks.every(t => t.status === 'done')) return;

    await updateDoc(doc(db, 'sections', sectionId), { status: 'completed', completedAt: Date.now() });

    const secQ = query(collection(db, 'sections'), where('projectId', '==', projectId), orderBy('order', 'asc'));
    const secSnap = await getDocs(secQ);
    const nextLocked = secSnap.docs.map(d => d.data()).find(s => s.status === 'locked');
    if (!nextLocked) return;

    await updateDoc(doc(db, 'sections', nextLocked.id), { status: 'active' });
    const blockedQ = query(collection(db, TASKS_COLLECTION), where('sectionId', '==', nextLocked.id));
    const blockedSnap = await getDocs(blockedQ);
    await Promise.all(blockedSnap.docs.map(d => {
      const task = d.data();
      if (task.status === 'blocked' && !task.blockedByTaskId) {
        return updateDoc(doc(db, TASKS_COLLECTION, d.id), { status: 'todo' });
      }
      return Promise.resolve();
    }));
  } catch (e) {
    console.error('Section completion check failed:', e);
  }
}

async function accumulateFocusedTime(sessionId, elapsed) {
  if (!sessionId || elapsed <= 0) return;
  try {
    const sessionSnap = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId));
    if (sessionSnap.exists()) {
      const prevFocused = sessionSnap.data().focusedTime || 0;
      await updateDoc(doc(db, SESSIONS_COLLECTION, sessionId), { focusedTime: prevFocused + elapsed });
    }
  } catch (e) {
    console.error('Failed to accumulate focusedTime:', e);
  }
}

// Duration (ms) between two "HH:MM" strings. Wraps past midnight so an entry whose end
// reads "earlier" than its start is treated as crossing into the next day (overnight shift).

export function TaskProvider({ children }) {
  const [state, dispatch] = useReducer(taskReducer, initialState);
  const { deptForProject, typeForProject, departmentActive, currentDepartmentId, departmentMode, rawDepartments } = useDepartments();
  const { projects: allProjects } = useProjects();
  // Department mode (D3): a new top-level task with no department of its own goes in the one you're in (All → General),
  // or its project's home department when the project says so; a project locked to a department always gives its own
  // (Project settings, shared/projectsettings.js).
  // v0.1.242: with the mode on, the department is inferred from the task's nature (shared/deptinfer.js: lock → picked →
  // project home → tags → title words, your own picks first → the department you're in) and the guess kept on the task
  // (`departmentGuess`) for its chip. A pick on the chip (`_deptPicked: 'pick' | 'correction'`) teaches meta/deptLearn.
  const { model: deptLearnModel_, learn: learnDepartment } = useDeptLearn();
  const opsTags = useLocalCollection('tags');
  const listTemplates = useLocalCollection('listTemplates');
  const guessDepartment = useCallback((t) => inferTaskDepartment(t, {
    departments: rawDepartments || [], project: t?.projectId ? allProjects.find((p) => p.id === t.projectId) : null,
    currentDepartmentId, learn: deptLearnModel_, opsTags, active: departmentActive,
  }), [rawDepartments, allProjects, currentDepartmentId, deptLearnModel_, opsTags, departmentActive]);
  const withDept = useCallback((input) => {
    const { _deptPicked, ...t } = input || {};
    if (_deptPicked && !t.parentId && t.departmentId !== undefined) learnDepartment(t.title, t.departmentId, { weight: _deptPicked === 'correction' ? 2 : 1 });
    // Checklists, routine steps, quick tasks and process steps keep the plain rule (lock → home → the department you're in).
    if (t.bucket || t.runId || t.quickTaskId || t.processId) {
      const d = newTaskDepartmentId(t, { project: t?.projectId ? allProjects.find((p) => p.id === t.projectId) : null, departmentActive, currentDepartmentId });
      return d === undefined ? t : { ...t, departmentId: d };   // null = General (a project locked to General)
    }
    const g = guessDepartment(t);
    const out = g ? { ...t, ...deptGuessStamp(g) } : t;
    // v0.1.247: then the department's task defaults (tags, photo, unit, list, note on done) and the project's "when a
    // task is added" (tags, list) — shared/processes.js captureDefaults.
    const project = out.projectId ? allProjects.find((p) => p.id === out.projectId) : null;
    const deptId = out.departmentId !== undefined ? (out.departmentId || GENERAL_DEPARTMENT_ID) : departmentActive ? GENERAL_DEPARTMENT_ID : (project?.departmentId || GENERAL_DEPARTMENT_ID);
    const dept = (rawDepartments || []).find((d) => d.id === deptId) || null;
    const { fields } = captureDefaults(out, { dept, project, listTemplates });
    return Object.keys(fields).length ? { ...out, ...fields } : out;
  }, [departmentActive, currentDepartmentId, allProjects, guessDepartment, learnDepartment, rawDepartments, listTemplates]);
  // D5: department mode stamps the session per task (each entry carries its own department); off = the project's.
  sessionStamp = (pid, taskId = null) => {
    if (departmentMode) return sessionDepartmentStamp({ tasks: state.tasks, projects: allProjects, departments: rawDepartments || [], departmentMode }, pid, taskId);
    const d = deptForProject(pid);
    return { departmentId: d.id, ...(d.feedBooks === false ? { feedBooks: false } : {}) };
  };
  const [pendingAfterPhoto, setPendingAfterPhoto] = useState(null);   // task awaiting an after/proof photo (any completion path)
  const clearAfterPhoto = useCallback(() => setPendingAfterPhoto(null), []);
  // CFO 4.4: a task done whose template asks for a value (paint used…) — the capture sheet asks (any completion path).
  const [pendingCapture, setPendingCapture] = useState(null);
  const clearCapture = useCallback(() => setPendingCapture(null), []);
  // v0.1.247: a task done whose department asks for a note on done (taskDefaults.noteOnDone, or the task's noteOnDone).
  const [pendingNote, setPendingNote] = useState(null);
  const clearNote = useCallback(() => setPendingNote(null), []);
  // What a finished task's project ("when a task is done") and department (task defaults) ask: tags, a photo, a note.
  const afterDone = (task) => {
    if (!task) return;
    const project = task.projectId ? allProjects.find((p) => p.id === task.projectId) : null;
    const deptId = departmentMode ? (task.departmentId || GENERAL_DEPARTMENT_ID) : (project?.departmentId || GENERAL_DEPARTMENT_ID);
    const r = doneRules(task, { project, dept: (rawDepartments || []).find((d) => d.id === deptId) || null });
    if (r.tags) updateTaskInFirestore(task.id, { tags: r.tags });
    if (r.photo && !(task.requiresAfterPhoto && !task.afterPhoto)) setPendingAfterPhoto(task);
    if (r.note) setPendingNote(task);
  };
  const asks = (task) => Array.isArray(task?.metrics) && task.metrics.some((m) => m?.source === 'ask');

  // ---- Derived timer/lock values (single source of truth = meta/timer + meta/lock) ----
  const timer = state.timer;
  const lock = state.lock;
  // Timer's task wins (it's the live working task); lock is the fallback when a task
  // is locked but the card timer hasn't been pointed at it yet.
  const activeTaskId = timer?.taskId ?? (lock?.active ? lock.taskId : null) ?? null;
  const currentTaskStart = timer?.startedAt ?? null;
  const accumulatedPause = timer?.accumulatedPause ?? 0;
  const lockedTaskId = lock?.active ? (lock.taskId ?? null) : null;

  // The phone's native follow-ups (live notice, focus, reminders, notification permission) live in
  // mobile components/NativeTaskEffects.js, mounted inside this provider.
  const pauseStatus = useMemo(() => (
    timer?.pauseType
      ? { type: timer.pauseType, startedAt: timer.pauseStartedAt, taskId: timer.taskId, context: timer.pauseContext || null }
      : null
  ), [timer?.pauseType, timer?.pauseStartedAt, timer?.taskId, timer?.pauseContext]);

  // Capture how work flows WITHIN A PROJECT. Paging "next" reassigns the active task each tap
  // (ProjectScreen startCard), so we settle-detect: a task you stay on ≥ SETTLE_MS is a real
  // 'switch' (you worked it); a task you land on and leave sooner is a 'skip' (considered &
  // passed — useful signal). Every event is keyed by the project you're working in.
  const committedRef = useRef(undefined);    // the task you're actually working
  const committedSinceRef = useRef(Date.now());
  const leftCommittedAtRef = useRef(null);   // when you first wandered off the committed task
  const pendingRef = useRef(null);           // a card you're eyeing but haven't settled on
  const pendingSinceRef = useRef(Date.now());
  const settleTimerRef = useRef(null);
  const SETTLE_MS = 60000;                   // ≥1 min on a card = real work; less = a pass
  const SKIP_FLOOR_MS = 5000;                // <5s = a flick past (navigation); not even a 'skip'
  useEffect(() => {
    if (committedRef.current === undefined) { committedRef.current = activeTaskId; committedSinceRef.current = Date.now(); return; }
    const now = Date.now();
    if (settleTimerRef.current) { clearTimeout(settleTimerRef.current); settleTimerRef.current = null; }

    // Left a card we actually looked at (≥5s) before it settled → 'skip' (considered, passed).
    // Faster than that is just flicking through the list — pure navigation, not worth a row.
    const pend = pendingRef.current;
    if (pend && pend !== activeTaskId && (now - pendingSinceRef.current) >= SKIP_FLOOR_MS) {
      const pTask = state.tasks.find((t) => t.id === pend);
      const cmt = committedRef.current ? state.tasks.find((t) => t.id === committedRef.current) : null;
      logTaskTransition({
        kind: 'skip',
        projectId: state.clockedIn?.projectId || pTask?.projectId || null,
        fromTaskId: committedRef.current || null, fromProjectId: cmt?.projectId || null,
        toTaskId: pend, toProjectId: pTask?.projectId || null,
        dwellMs: now - pendingSinceRef.current,
        sessionId: state.clockedIn?.sessionId || null,
      });
    }
    pendingRef.current = null;

    if (activeTaskId === committedRef.current) { leftCommittedAtRef.current = null; return; } // back on real task

    // A different task is showing — start the settle clock.
    if (leftCommittedAtRef.current == null) leftCommittedAtRef.current = now;
    pendingRef.current = activeTaskId;
    pendingSinceRef.current = now;
    const candidate = activeTaskId;
    const candidateSince = now;
    const from = committedRef.current;
    const committedSince = committedSinceRef.current;
    const leftAt = leftCommittedAtRef.current;
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      const fromTask = from ? state.tasks.find((t) => t.id === from) : null;
      const toTask = candidate ? state.tasks.find((t) => t.id === candidate) : null;
      logTaskTransition({
        kind: 'switch',
        projectId: state.clockedIn?.projectId || toTask?.projectId || null,
        fromTaskId: from || null, fromProjectId: fromTask?.projectId || null,
        fromStatus: fromTask?.status || null,
        fromActiveMs: leftAt - committedSince,   // time actually worked, excluding the browse
        toTaskId: candidate || null, toProjectId: toTask?.projectId || null,
        toResumed: !!(toTask && (toTask.timeSpent || 0) > 0),
        sessionId: state.clockedIn?.sessionId || null,
      });
      committedRef.current = candidate;
      committedSinceRef.current = candidateSince;
      leftCommittedAtRef.current = null;
      pendingRef.current = null;
    }, SETTLE_MS);
    return () => { if (settleTimerRef.current) { clearTimeout(settleTimerRef.current); settleTimerRef.current = null; } };
  }, [activeTaskId]);  

  // Real-time Firestore listeners
  useEffect(() => {
    const q = query(collection(db, TASKS_COLLECTION), orderBy('createdAt', 'asc'));
    const unsubTasks = onSnapshot(q, (snapshot) => {
      const tasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      dispatch({ type: 'LOAD_TASKS', tasks });
    }, (err) => {
      console.error('Firestore listener error:', err);
      dispatch({ type: 'LOAD_TASKS', tasks: [] });
    });

    const sessionsQ = query(collection(db, SESSIONS_COLLECTION), orderBy('startedAt', 'asc'));
    // The org's sessions arrive together; the clock, day reports and time screens use this member's own
    // (ownSessions) — a teammate's clock is theirs. The Team page and job cost read them all (allClockSessions).
    const unsubSessions = onSnapshot(sessionsQ, (snapshot) => {
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      dispatch({ type: 'LOAD_SESSIONS', sessions: ownSessions(all, currentUid()), all });
    });

    const unsubClock = onSnapshot(doc(db, META_COLLECTION, 'clock'), (snapshot) => {
      if (snapshot.exists()) {
        dispatch({ type: 'SET_CLOCK', clock: hydrateClock(snapshot.data()) });
      }
    });

    const unsubLock = onSnapshot(doc(db, META_COLLECTION, 'lock'), (snapshot) => {
      dispatch({ type: 'SET_LOCK', lock: snapshot.exists() ? snapshot.data() : null });
    });

    // meta/timer is the source of truth — store it verbatim (survives refresh/rebundle)
    const unsubTimer = onSnapshot(doc(db, META_COLLECTION, 'timer'), (snapshot) => {
      dispatch({ type: 'SET_TIMER', timer: snapshot.exists() ? snapshot.data() : null });
    });

    return () => { unsubTasks(); unsubSessions(); unsubClock(); unsubLock(); unsubTimer(); };
  }, []);

  // Re-sync the source docs whenever the app returns to the foreground. onSnapshot
  // normally redelivers on reconnect, but an explicit refetch guarantees the canonical
  // timer/lock/clock state is current before any timer math runs — this is what
  // prevents "minimizing changes the locked task".
  useEffect(() => {
    return platform().appState.subscribe((next) => {
      if (next !== 'active') return;
      getDoc(doc(db, META_COLLECTION, 'timer'))
        .then(snap => dispatch({ type: 'SET_TIMER', timer: snap.exists() ? snap.data() : null }))
        .catch(() => {});
      getDoc(doc(db, META_COLLECTION, 'lock'))
        .then(snap => dispatch({ type: 'SET_LOCK', lock: snap.exists() ? snap.data() : null }))
        .catch(() => {});
      getDoc(doc(db, META_COLLECTION, 'clock'))
        .then(snap => {
          if (!snap.exists()) return;
          dispatch({ type: 'SET_CLOCK', clock: hydrateClock(snap.data()) });
        })
        .catch(() => {});
    });
  }, []);

  // Migration: if clocked in but no timer doc, create one
  useEffect(() => {
    if (state.loaded && state.timerRestored && state.clockedIn && !timer?.startedAt) {
      saveTimerState(makeTimerState({ startedAt: Date.now() }));
    }
  }, [state.loaded, state.timerRestored, state.clockedIn, timer?.startedAt]);

  // The task doc's shape and defaults live in shared/commands (the MCP adds tasks the same way).
  const addTask = useCallback((task) => {
    const { writes, task: newTask } = commands.addTask({ tasks: state.tasks, newId }, withDept(task));
    dispatch({ type: 'SET_TASKS', tasks: [...state.tasks, newTask] });
    commitWrites(writes).catch((e) => console.error('addTask failed:', e));
    // Fire-and-forget: keep a durable copy of any local photo and queue its upload
    keepMedia(newTask.id, null, newTask);
    return newTask;
  }, [state.tasks, withDept]);

  // Several tasks at once (photo-first capture): sequential sortOrder, one local batch = one
  // outbox group, so they land together and in order. Same defaults as addTask.
  const addTasks = useCallback(async (list) => {
    const { writes, tasks: made } = commands.addTasks({ tasks: state.tasks, newId }, (list || []).map(withDept));
    if (!made.length) return [];
    dispatch({ type: 'SET_TASKS', tasks: [...state.tasks, ...made] });
    await commitWrites(writes).catch((e) => console.error('addTasks failed:', e));
    made.forEach((t) => keepMedia(t.id, null, t));
    return made;
  }, [state.tasks, withDept]);

  // Today's setup or cleanup checklist for a project (from its type, or its own list): create the
  // missing items and carry yesterday's unticked ones forward — one batch. No-op without steps.
  const tasksRef = useRef(state.tasks);
  tasksRef.current = state.tasks;
  const ensureBucketTasks = useCallback(async (projectId, bucket) => {
    if (!projectId) return [];
    const project = allProjects.find((p) => p.id === projectId);
    if (!project || !automationRunsBucket(project, bucket)) return [];   // the project turned this checklist off (automations)
    const date = localToday();
    const type = typeForProject(projectId);
    const { create, redate } = planBucketTasks({ project, type, bucket, date, tasks: tasksRef.current });
    if (!create.length && !redate.length) return [];
    const now = Date.now();
    const dept = departmentMode ? bucketDepartmentId(project, type) : null;   // D5: in a department, not General
    const made = create.map((t, i) => makeTaskDoc(dept ? { ...t, departmentId: dept } : t, [], now + i));
    const batch = writeBatch(db);
    made.forEach((t) => batch.set(doc(db, TASKS_COLLECTION, t.id), t));
    redate.forEach((id) => batch.update(doc(db, TASKS_COLLECTION, id), { bucketDate: date, scheduledDate: date }));
    const redated = new Set(redate);
    dispatch({ type: 'SET_TASKS', tasks: [...tasksRef.current.map((t) => (redated.has(t.id) ? { ...t, bucketDate: date, scheduledDate: date } : t)), ...made] });
    await batch.commit().catch((e) => console.error('checklist create failed:', e));
    return made;
  }, [allProjects, typeForProject, departmentMode]);

  const updateTask = useCallback((input) => {
    let task = input;
    const cur0 = input?.id ? state.tasks.find((t) => t.id === input.id) : null;
    if (cur0?.scopeLocked && 'scope' in input && (input.scope || null) !== (cur0.scope || null)) return;   // locked in the quote: unlock first
    // A department changed by hand on a top-level task = a pick (a correction when it was a guess): teach it, drop the guess.
    if (cur0 && !cur0.parentId && 'departmentId' in input && (input.departmentId || null) !== (cur0.departmentId || null)) {
      learnDepartment(input.title || cur0.title, input.departmentId || null, { weight: cur0.departmentGuess ? 2 : 1 });
      if (cur0.departmentGuess) task = { ...input, departmentGuess: null };
    }
    const { id, ...updates } = task;
    const cur = cur0;
    // A move to another project takes the subtasks along (one group, shared command).
    if (cur && 'projectId' in updates && (updates.projectId || null) !== (cur.projectId || null)) {
      const r = commands.updateTask({ tasks: state.tasks }, { id, ...updates });
      const patched = new Map(r.writes.map((w) => [w.path.slice(6), w.data]));
      dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...task, ...patched.get(id) } : patched.has(t.id) ? { ...t, ...patched.get(t.id) } : t)) });
      commitWrites(r.writes).catch((e) => console.error('task move failed:', e));
      return;
    }
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.map(t => t.id === task.id ? { ...t, ...task } : t) });
    // Use updateDoc with only the changed fields to avoid overwriting concurrent web edits
    if (id) {
      updateTaskInFirestore(id, updates);
      // Photos arrive as picker/camera paths: one durable copy each, swapped in, queued for upload.
      if ('photo' in task || 'shots' in task || 'afterPhotos' in task || 'afterPhoto' in task) keepMedia(id, cur || null, { ...(cur || {}), ...task });
    }
  }, [state.tasks, learnDepartment]);

  // Department mode: move tasks (top-level only; subtasks follow) to a department, one group. → { moved, skipped, locked }
  // (locked = in a project locked to its department — those stay; move them to another project.)
  const moveTasksToDepartment = useCallback(async (ids, departmentId) => {
    const { writes, moved, skipped, locked } = moveTasksToDepartment_({ tasks: tasksRef.current, projects: allProjects }, { ids, departmentId });
    if (!writes.length) return { moved, skipped, locked };
    const patch = new Map(writes.map((w) => [w.path.slice('tasks/'.length), w.data]));
    dispatch({ type: 'SET_TASKS', tasks: tasksRef.current.map((t) => (patch.has(t.id) ? { ...t, ...patch.get(t.id) } : t)) });
    await commitWrites(writes).catch((e) => console.error('moveTasksToDepartment failed:', e));
    return { moved, skipped, locked };
  }, [allProjects]);

  // Append a free-form note (string) to a task's notes array.
  // noteEvents carries WHEN each note was written (the Daily Log files notes by day); notes[] stays
  // the plain string list every existing renderer reads. Logic: shared/commands addTaskNote.
  const addNote = useCallback((taskId, text, extra = {}) => {
    if (!taskId || !String(text || '').trim() || !state.tasks.some((t) => t.id === taskId)) return;
    const { writes } = commands.addTaskNote({ tasks: state.tasks, clock: state.clockedIn }, { id: taskId, text, kind: extra.kind || null });
    const patch = writes[0].data;
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) });
    commitWrites(writes).catch((e) => console.error('addNote failed:', e));
  }, [state.tasks, state.clockedIn]);

  // Outline move (up / down / indent / outdent) within a project — see planTaskMove.
  // One local batch = one outbox group, so the move lands on the server whole.
  const moveTask = useCallback((taskId, dir, sections = []) => {
    if (!state.tasks.some((t) => t.id === taskId)) return false;
    const { writes, moved } = commands.moveTask({ tasks: state.tasks, sections }, { id: taskId, dir });
    if (!moved) return false;
    const byPath = new Map(writes.map((w) => [w.path, w.data]));
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (byPath.has(`tasks/${t.id}`) ? { ...t, ...byPath.get(`tasks/${t.id}`) } : t)) });
    commitWrites(writes).catch((e) => console.error('Task move failed:', e));
    return true;
  }, [state.tasks]);

  // Drag and drop in the list: `rows` = the outline as shown without the dragged task's subtree,
  // `at` = drop position, `depth` = the depth dragged to (shared/indexing planTaskDrop). One batch.
  const dropTask = useCallback((taskId, rows, at, depth, sections = []) => {
    const task = state.tasks.find((t) => t.id === taskId);
    if (!task) return false;
    const scope = state.tasks.filter((t) => t.projectId === task.projectId);
    const writes = planTaskDrop(scope, sections, taskId, rows, at, depth);
    if (!writes.length) return false;
    const now = Date.now();
    const byId = new Map(writes.map((w) => [w.id, w.patch]));
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (byId.has(t.id) ? { ...t, ...byId.get(t.id), updatedAt: now } : t)) });
    const batch = writeBatch(db);
    writes.forEach((w) => batch.update(doc(db, TASKS_COLLECTION, w.id), { ...w.patch, updatedAt: now }));
    batch.commit().catch((e) => console.error('Task drop failed:', e));
    return true;
  }, [state.tasks]);

  const reorderTasks = useCallback((orderedIds) => {
    const updates = {};
    orderedIds.forEach((id, i) => { updates[id] = (i + 1) * 1000; });   // gapped, as planTaskMove
    const newTasks = state.tasks.map(t =>
      updates[t.id] !== undefined ? { ...t, sortOrder: updates[t.id] } : t
    );
    dispatch({ type: 'SET_TASKS', tasks: newTasks });
    orderedIds.forEach((id) => updateTaskInFirestore(id, { sortOrder: updates[id] }));
  }, [state.tasks]);

  // Day-execution override: pin these tasks to an explicit order (voice/drag in the
  // day view). manualOrder beats the computed day pipeline (see resolveDayOrder).
  const setManualOrder = useCallback((orderedIds) => {
    const updates = {};
    orderedIds.forEach((id, i) => { updates[id] = i; });
    const newTasks = state.tasks.map(t => (updates[t.id] !== undefined ? { ...t, manualOrder: updates[t.id] } : t));
    dispatch({ type: 'SET_TASKS', tasks: newTasks });
    orderedIds.forEach((id, i) => {
      const task = newTasks.find(t => t.id === id);
      if (task) saveTaskToFirestore({ ...task, manualOrder: i });
    });
  }, [state.tasks]);

  // Drop the override → tasks fall back to the systematic computed order.
  const clearManualOrder = useCallback((ids) => {
    const idSet = new Set(ids);
    const newTasks = state.tasks.map(t => (idSet.has(t.id) ? { ...t, manualOrder: null } : t));
    dispatch({ type: 'SET_TASKS', tasks: newTasks });
    ids.forEach((id) => {
      const task = newTasks.find(t => t.id === id);
      if (task) saveTaskToFirestore({ ...task, manualOrder: null });
    });
  }, [state.tasks]);

  const deleteTask = useCallback((id) => {
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.filter(t => t.id !== id) });
    deleteTaskFromFirestore(id);
  }, [state.tasks]);   // single doc; deleteTaskTree removes a subtree in one group (shared/commands)

  const buildTimeEntry = (elapsed) => {
    const now = Date.now();
    const startMs = now - elapsed;
    const sd = new Date(startMs);
    const ed = new Date(now);
    const p = (n) => String(n).padStart(2, '0');
    return {
      id: newId(),
      date: `${sd.getFullYear()}-${p(sd.getMonth() + 1)}-${p(sd.getDate())}`,
      startTime: `${p(sd.getHours())}:${p(sd.getMinutes())}`,
      endTime: `${p(ed.getHours())}:${p(ed.getMinutes())}`,
      duration: elapsed,
      note: null,
      manual: false,
      createdAt: now,
    };
  };

  // Reset the card timer NOW, synchronously, before any await. Card UIs re-point the
  // timer to the next card off this same render — doing the reset pre-await means there
  // is no post-await timer write to race the next-card start.
  const resetCardTimer = () => {
    const reset = makeTimerState({ startedAt: Date.now() });
    dispatch({ type: 'SET_TIMER', timer: reset });
    saveTimerState(reset);
  };

  // The clock / timer commands (shared/commands) read the same docs the reducer holds. `stampFor` /
  // `typeFor` hand them the department + project-type lookups this provider already has.
  const cmdCtx = (over = {}) => {
    const cl = clockRef.current || state.clockedIn;
    return {
      tasks: state.tasks, projects: allProjects, sessions: state.clockSessions, timer, lock, clock: cl ? { ...cl, active: true } : null,
      now: Date.now, newId,
      stampFor: (pid, taskId = null) => sessionStamp(pid, taskId),
      departmentMode, departments: rawDepartments || [],
      typeFor: (project) => typeForProject(project?.id),
      ...over,
    };
  };

  // A department's quick task (shared/quicktasks.js): today's instance in the job you're on or the department's home
  // project, the clock on it — one tap. Throws with what's missing (no job, no home project).
  const startQuickTask = (departmentId, quickTaskId, projectId = null) => runClockCommand(commands.startQuickTask, { departmentId, quickTaskId, projectId });

  // Run a shared command that may move the clock / timer / lock (routines): the new clock and timer show at
  // once, the writes land as one group. `over` adds what the command reads beyond tasks + clock (routines, runs…).
  const runClockCommand = (fn, input, over = {}) => {
    const c0 = cmdCtx(over);
    const r = fn(c0, input);
    // The meta docs as the writes leave them — an `update` (e.g. a re-filed session moving the clock's project) merges.
    const touched = (path) => r.writes.some((w) => w.path === path && w.op !== 'delete');
    const after = applyWrites(new Map([['meta/timer', c0.timer], ['meta/lock', c0.lock], ['meta/clock', c0.clock]].filter(([, v]) => v)), r.writes.filter((w) => w.path.startsWith('meta/')));
    if (touched('meta/timer')) dispatch({ type: 'SET_TIMER', timer: after.get('meta/timer') });
    if (touched('meta/lock')) dispatch({ type: 'SET_LOCK', lock: after.get('meta/lock') });
    const cw = touched('meta/clock') ? after.get('meta/clock') : null;
    if (cw) {
      if (cw.active === false) { clockRef.current = null; dispatch({ type: 'CLOCK_OUT' }); }
      else { const { active, ...c } = cw; clockRef.current = c; dispatch({ type: 'SET_CLOCK', clock: c }); }
    }
    commitWrites(r.writes).then(() => { if (cw?.active === false) getSync()?.syncNow(); }).catch((e) => console.error('command failed:', e));
    return r;
  };

  // Time is only ever banked to a task when ALL of these hold: you're on the clock, the timer
  // started inside this clock session (a timer left over from yesterday's clock-out is not work),
  // and the timer is timing THIS task (or no task yet — the card timer before a task is picked).
  // Anything else completes/skips with zero time and leaves the running timer alone.
  // Pause time is already written to the task by resumeTask, so it is never banked again here.
  const bankableFor = (id) => {
    const r = bankable({ timer, clockedIn: state.clockedIn, taskId: id });
    const task = state.tasks.find((t) => t.id === id);
    const entry = r.elapsed > 0 ? buildTimeEntry(r.elapsed) : null;
    return { ...r, timeEntry: stampEntryDepartment(entry, task, { departmentMode, tasks: state.tasks, projects: allProjects, departments: rawDepartments || [] }) };
  };

  // Bank whatever the timer has on its current task without completing or unlocking it.
  // Call before anything that overwrites the timer (starting another card, switching project).
  const bankCurrent = async () => {
    if (!timer?.taskId) return;
    const { elapsed, timeEntry } = bankableFor(timer.taskId);
    if (elapsed > 0) {
      await atomicStopTimer({ taskId: timer.taskId, elapsed, sessionId: state.clockedIn?.sessionId || null, timeEntry, keepTimer: true, keepLock: true })
        .catch((e) => console.error('bankCurrent failed:', e));
    }
  };

  const completeTask = useCallback(async (id, opts) => {
    const task = state.tasks.find(t => t.id === id);
    const { elapsed, timeEntry, owns } = opts?.noTime ? { elapsed: 0, timeEntry: null, owns: false } : bankableFor(id);
    const pauseMs = 0;
    // Optimistic: hide the card + reset the timer now.
    dispatch({ type: 'COMPLETE_TASK', id });
    if (task?.requiresAfterPhoto && !task.afterPhoto) setPendingAfterPhoto(task);   // prompt for proof photo (works from any view)
    if (asks(task)) setPendingCapture(task);
    afterDone(task);
    if (owns) resetCardTimer();
    try {
      await atomicCompleteTask({
        taskId: id,
        elapsed,
        pauseTime: pauseMs,
        sessionId: state.clockedIn?.sessionId || null,
        timeEntry,
        allTasks: state.tasks,
      });
    } catch (e) {
      console.error('atomicCompleteTask failed, falling back to local:', e);
      if (task) {
        const prevEntries = task.timeEntries || [];
        const newEntries = timeEntry ? [...prevEntries, timeEntry] : prevEntries;
        saveTaskToFirestore({
          ...task, status: 'done', completedAt: Date.now(),
          timeSpent: (task.timeSpent || 0) + elapsed,
          pauseTime: (task.pauseTime || 0) + pauseMs,
          timeEntries: newEntries,
        });
        accumulateFocusedTime(state.clockedIn?.sessionId, elapsed);
        unblockDependentTasks(id);
        if (task.sectionId) checkSectionCompletion(task.sectionId, task.projectId);
      }
      if (lock?.active && lock.taskId === id) {
        setDoc(doc(db, META_COLLECTION, 'lock'), { active: false, taskId: null });
      }
    }
    if (task && !task.bucket) {
      fireProcessEvent('task_complete', { projectId: task.projectId || null, taskId: id });
      // Phase photo: the type asks for one when the last open step of a phase is done.
      const type = task.projectId ? typeForProject(task.projectId) : null;
      const alreadyAsking = task.requiresAfterPhoto && !task.afterPhoto;
      if (type?.capture?.phasePhoto && task.sectionId && !task.parentId && !alreadyAsking) {
        const left = state.tasks.filter((t) => t.sectionId === task.sectionId && !t.parentId && !t.bucket && t.id !== id && t.status !== 'done');
        if (!left.length) {
          getDoc(doc(db, 'sections', task.sectionId))
            .then((snap) => setPendingAfterPhoto({ ...task, _phaseTitle: snap.exists() ? snap.data().title : 'Phase' }))
            .catch(() => {});
        }
      }
    }
  }, [state.tasks, state.clockedIn, timer, lock, typeForProject, allProjects, rawDepartments, departmentMode]);

  const skipTask = useCallback(async (id) => {
    const task = state.tasks.find(t => t.id === id);
    const { elapsed, timeEntry, owns } = bankableFor(id);
    const pauseMs = 0;
    dispatch({ type: 'SKIP_TASK', id });
    if (owns) resetCardTimer();
    try {
      // atomicStopTimer resets meta/timer + clears meta/lock atomically, and (with the
      // server-side extension) writes the skip metadata in the same batch.
      await atomicStopTimer({
        taskId: id,
        elapsed,
        pauseTime: pauseMs,
        sessionId: state.clockedIn?.sessionId || null,
        timeEntry,
        skippedCount: (task?.skippedCount || 0) + 1,
        lastSkippedAt: Date.now(),
        keepTimer: !owns,
        keepLock: !owns && lock?.taskId !== id,
      });
    } catch (e) {
      console.error('atomicStopTimer failed, falling back to local:', e);
      if (task) {
        const prevEntries = task.timeEntries || [];
        const newEntries = timeEntry ? [...prevEntries, timeEntry] : prevEntries;
        saveTaskToFirestore({
          ...task,
          timeSpent: (task.timeSpent || 0) + elapsed,
          pauseTime: (task.pauseTime || 0) + pauseMs,
          timeEntries: newEntries,
          skippedCount: (task.skippedCount || 0) + 1,
          lastSkippedAt: Date.now(),
        });
        accumulateFocusedTime(state.clockedIn?.sessionId, elapsed);
      }
      setDoc(doc(db, META_COLLECTION, 'lock'), { active: false, taskId: null });
    }
  }, [state.tasks, state.clockedIn, timer, lock]);

  const resetTask = useCallback((id) => {
    dispatch({ type: 'RESET_TASK', id });
    if (state.tasks.some((t) => t.id === id)) updateTaskInFirestore(id, { status: 'todo', completedAt: null, completion: null, updatedAt: Date.now() });
  }, [state.tasks]);

  // ── Manual time-entry editing (retroactive fixes from the Time editor) ────────────────────────
  // Add / edit / delete a single time entry on a task. All three keep task.timeSpent in sync by
  // the DURATION DELTA — never by recomputing from the timeEntries array. Older tasks carry
  // timeSpent that predates timeEntries, and a full recompute would silently wipe it. This mirrors
  // the web TimeEditor and the server callable behavior. Durations are MILLISECONDS.
  // Every change to logged time lands WITH its ledger record (timeEdits, shared/timeledger) in one
  // batch — the totals and the record of how they got there can't disagree.
  // One group each: the task patches + the ledger record (shared/commands timeentries).
  const commitTime = useCallback(async (r) => {
    if (!r) return null;
    const byPath = new Map(r.writes.filter((w) => w.op === 'update' && w.path.startsWith('tasks/')).map((w) => [w.path.slice(6), w.data]));
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (byPath.has(t.id) ? { ...t, ...byPath.get(t.id) } : t)) });
    const who = await loadIdentity().catch(() => null);
    const rec = r.writes.find((w) => w.path.startsWith('timeEdits/'));
    if (rec && who) rec.data.by = { uid: who.uid || null, name: who.name || who.email || null };
    await commitWrites(r.writes).catch((e) => console.error('time change failed:', e));
    return r;
  }, [state.tasks]);
  const timeCtx = () => ({ tasks: state.tasks, now: Date.now, newId, by: null });

  const addTimeEntry = useCallback((taskId, entry) => {
    let r;
    try { r = commands.addTimeEntry(timeCtx(), { taskId, date: entry.date, startTime: entry.startTime, endTime: entry.endTime, note: entry.note || null }); }
    catch (e) { console.error('addTimeEntry refused:', e); return null; }
    commitTime(r);
    return r.entry;
  }, [state.tasks, commitTime]);  

  const updateTimeEntry = useCallback((taskId, entryId, patch) => {
    let r;
    try { r = commands.editTimeEntry(timeCtx(), { taskId, entryId, ...patch }); }
    catch (e) { console.error('updateTimeEntry refused:', e); return; }
    commitTime(r);
  }, [state.tasks, commitTime]);  

  const deleteTimeEntry = useCallback((taskId, entryId) => {
    let r;
    try { r = commands.deleteTimeEntry(timeCtx(), { taskId, entryId }); }
    catch (e) { console.error('deleteTimeEntry refused:', e); return; }
    commitTime(r);
  }, [state.tasks, commitTime]);  

  // A logged stretch landed on the wrong task: move it (entry + its duration) in ONE write, so the
  // two totals can never disagree.
  const moveTimeEntry = useCallback(async (fromTaskId, entryId, toTaskId) => {
    let r;
    try { r = commands.moveTimeEntry(timeCtx(), { fromTaskId, entryId, toTaskId }); }
    catch (e) { console.error('moveTimeEntry refused:', e); return false; }
    await commitTime(r);
    return true;
  }, [state.tasks, commitTime]);  

  // The timer was running on the wrong task: point it at the right one WITHOUT banking — the whole
  // running stretch (its start and pauses) belongs to `taskId` now.
  const retargetTimer = useCallback(async (taskId) => {
    if (!taskId || !timer?.startedAt) return;
    let r;
    try { r = commands.retargetTimer({ ...timeCtx(), timer }, { taskId }); }
    catch (e) { console.error('retargetTimer refused:', e); return; }
    dispatch({ type: 'SET_TIMER', timer: r.timer }); // optimistic
    const who = await loadIdentity().catch(() => null);
    const rec = r.writes.find((w) => w.path.startsWith('timeEdits/'));
    if (rec && who) rec.data.by = { uid: who.uid || null, name: who.name || who.email || null };
    await commitWrites(r.writes).catch((e) => console.error('retarget failed:', e));
  }, [timer, state.tasks]);  

  // Retroactively flip a task's done/undone state WITHOUT completeTask's timer + atomic side
  // effects — for fixing an accidental swipe-complete. Time already logged is preserved either way.
  const setTaskDone = useCallback((taskId, done) => {
    updateTask({ id: taskId, status: done ? 'done' : 'todo', completedAt: done ? Date.now() : null });
    const task = done ? state.tasks.find((t) => t.id === taskId) : null;
    if (asks(task)) setPendingCapture(task);
    afterDone(task);
  }, [updateTask, state.tasks, allProjects, rawDepartments, departmentMode]);

  const pauseTask = useCallback((taskId, reason, context = null) => {
    const base = timer?.startedAt ? timer : makeTimerState({ taskId, startedAt: null });
    let r;
    try { r = commands.pauseTask({ ...cmdCtx(), timer: { ...base, taskId } }, { type: reason, context }); }
    catch (e) { // no live timer: pause an empty one so the UI still shows the break (pre-existing behaviour)
      r = { writes: [], timer: makeTimerState({ taskId, startedAt: timer?.startedAt ?? null, pauseType: reason, pauseStartedAt: Date.now(), pauseContext: context, accumulatedPause: timer?.accumulatedPause || 0 }) };
      saveTimerState(r.timer);
    }
    dispatch({ type: 'SET_TIMER', timer: r.timer }); // optimistic
    if (r.writes.length) commitWrites(r.writes).catch((e) => console.error('pauseTask failed:', e));
  }, [timer]);  

  const resumeTask = useCallback(() => {
    if (!timer?.pauseType || !timer?.pauseStartedAt) return;
    const r = commands.resumeTask(cmdCtx());
    const patch = r.writes.find((w) => w.path.startsWith('tasks/'))?.data;
    if (patch) dispatch({ type: 'SET_TASKS', tasks: state.tasks.map((t) => (t.id === timer.taskId ? { ...t, ...patch } : t)) });
    dispatch({ type: 'SET_TIMER', timer: r.timer }); // optimistic
    commitWrites(r.writes).catch((e) => console.error('resumeTask failed:', e));
  }, [timer, state.tasks]);  

  const lockTask = useCallback((taskId) => {
    const newLock = { active: true, taskId };
    dispatch({ type: 'SET_LOCK', lock: newLock }); // optimistic
    setDoc(doc(db, META_COLLECTION, 'lock'), newLock);
    // Also ensure we're clocked in
    if (!state.clockedIn) {
      const task = state.tasks.find(t => t.id === taskId);
      if (task?.projectId) {
        const pid = String(task.projectId);
        const now = Date.now();
        const sessionId = newId();
        dispatch({ type: 'CLOCK_IN', projectId: pid, sessionId });
        saveClockState({ startedAt: now, projectId: pid, sessionId });
        saveClockSession({
          id: sessionId, projectId: pid, startedAt: now, endedAt: null,
          duration: 0, focusedTime: 0, date: localToday(),
        });
      }
    }
    // Always a fresh timer for the task being locked, unless it is already the one being timed in
    // this session. A timer left over from the last clock-out must never carry into today.
    const live = state.clockedIn && timer?.startedAt && timer.startedAt >= (state.clockedIn.startedAt || 0) - 1000;
    if (!(live && timer.taskId === taskId)) {
      if (live) bankCurrent();
      const t = makeTimerState({ taskId, startedAt: Date.now() });
      dispatch({ type: 'SET_TIMER', timer: t }); // optimistic
      saveTimerState(t);
    }
  }, [state.clockedIn, state.tasks, timer]);

  const unlockTask = useCallback(() => {
    const newLock = { active: false, taskId: null };
    dispatch({ type: 'SET_LOCK', lock: newLock }); // optimistic
    setDoc(doc(db, META_COLLECTION, 'lock'), newLock);
  }, []);

  // opts.taskId: start that task's timer in the same step ("Clock in & start this task") — a separate
  // startCard after clockIn ran against the pre-clock-in state and was skipped, leaving no task timing.
  const clockIn = useCallback((projectId = null, opts = {}) => {
    const pid = projectId ? String(projectId) : null;
    let r;
    try { r = commands.clockIn(cmdCtx({ clock: null }), { projectId: pid, taskId: opts.taskId || null }); }
    catch (e) { console.error('clockIn refused:', e); return; }
    dispatch({ type: 'CLOCK_IN', projectId: pid, sessionId: r.session.id, mode: 'work' });
    dispatch({ type: 'SET_TIMER', timer: r.timer }); // optimistic
    if (r.setupTasks.length) dispatch({ type: 'SET_TASKS', tasks: [...state.tasks, ...r.setupTasks] });
    commitWrites(r.writes).catch((e) => console.error('clockIn failed:', e));
    fireProcessEvent('clock_in', { projectId: pid });
  }, [state.tasks, state.clockSessions, allProjects, deptForProject, typeForProject]);  

  // One group: the travel overlay closes, the work session ends, the live task's time is banked,
  // and clock / timer / lock reset — together or not at all (shared/commands clockOut).
  const clockOut = useCallback(async () => {
    const clocked = clockRef.current || state.clockedIn;
    if (!clocked) return;
    const segs = travelSegmentsRef.current;
    let r;
    try { r = commands.clockOut(cmdCtx(), { travelSegments: clocked.traveling ? [...segs] : null }); }
    catch (e) { console.error('clockOut refused:', e); return; }
    travelSegmentsRef.current = [];
    prevTravelTaskRef.current = undefined;
    dispatch({ type: 'CLOCK_OUT' });
    dispatch({ type: 'SET_TIMER', timer: makeTimerState() }); // optimistic
    try {
      await commitWrites(r.writes);
    } catch (e) {
      console.error('clockOut batch failed:', e);
      saveClockState(null);
      clearTimerState();
      setDoc(doc(db, META_COLLECTION, 'lock'), { active: false, taskId: null });
    }
    fireProcessEvent('clock_out', { projectId: clocked?.projectId || null });
    // Clock-out is the write that matters most at the end of a day — send it now, not after
    // the debounce (the phone usually goes in a pocket seconds later).
    getSync()?.syncNow();
  }, [state.clockedIn, state.tasks, state.clockSessions, timer, lock]);  

  const switchClockProject = useCallback((newProjectId, opts = {}) => {
    // clockRef, not the closure: callers like the arrival prompt run long after they were built.
    const cl = clockRef.current || state.clockedIn;
    if (!cl) return;
    const npid = newProjectId ? String(newProjectId) : null;
    let r;
    // Banks the live task, closes the old session (drops a <2 s stub), opens the new one, keeps an
    // in-flight travel overlay so "clock into this job while still driving" keeps recording.
    try { r = commands.switchProject(cmdCtx(), { projectId: npid, taskId: opts.taskId || null }); }
    catch (e) { console.error('switchClockProject refused:', e); return; }
    const { active, ...newClock } = r.clock;
    clockRef.current = newClock;
    dispatch({ type: 'SET_CLOCK', clock: newClock });
    const t = r.writes.find((w) => w.path === 'meta/timer')?.data || makeTimerState({ startedAt: Date.now() });
    dispatch({ type: 'SET_TIMER', timer: t }); // optimistic
    if (r.setupTasks.length) dispatch({ type: 'SET_TASKS', tasks: [...state.tasks, ...r.setupTasks] });
    commitWrites(r.writes).catch((e) => console.error('switchClockProject failed:', e));
    fireProcessEvent('clock_in', { projectId: npid });
  }, [state.clockedIn, state.tasks, state.clockSessions, timer, lock, allProjects]);  

  // ── Travel overlay ──────────────────────────────────────────────────────────────────────────
  // Travel is a concurrent OVERLAY on the work clock, NOT a mode that replaces it. You keep working
  // whatever task you're on while a travel session records the route + drive time, attributed via a
  // timestamped `taskSegments` array (switching tasks mid-drive appends a segment). Travel time is a
  // PARALLEL metric — never added into clocked/billable work time (session is overlay:true).
  // Full architecture: ~/.claude/plans/signo-travel-overlay.md
  const clockRef = useRef(null);
  clockRef.current = state.clockedIn;
  const travelSegmentsRef = useRef([]);      // local mirror of the open travel session's taskSegments
  const prevTravelTaskRef = useRef(undefined);

  // Append/close a task-attribution segment on the open travel session. No-op if the task is unchanged.
  const noteTravelTask = useCallback((taskId, projectId) => {
    const cl = clockRef.current;
    if (!cl?.traveling || !cl.travelSessionId) return;
    const segs = travelSegmentsRef.current;
    const last = segs[segs.length - 1];
    const tid = taskId || null;
    if (last && last.taskId === tid && !last.endedAt) return;   // same task already open
    const now = Date.now();
    if (last && !last.endedAt) last.endedAt = now;
    segs.push({ taskId: tid, projectId: projectId || null, startedAt: now, endedAt: null });
    updateDoc(doc(db, SESSIONS_COLLECTION, cl.travelSessionId), { taskSegments: [...segs] })
      .catch(e => console.error('travel segment update failed:', e));
  }, []);

  // `auto` = fired by the geofence, not by you. Auto-travel only ever overlays a clock that is
  // already running; it must never clock you in (a stale GPS callback after clock-out did exactly
  // that and produced an 8-hour "drive" of 21 m).
  const startTravel = useCallback((opts = {}) => {
    if (clockRef.current?.traveling) return;     // already traveling
    const now = Date.now();
    const date = localToday();
    // Ensure we're on the clock — supports "clock in to just travel" (heading to work, no task yet).
    let base = clockRef.current;
    if (!base) {
      if (opts.auto) return;
      base = { startedAt: now, projectId: null, sessionId: newId(), mode: 'work' };
      saveClockSession({ id: base.sessionId, projectId: null, type: 'work', startedAt: now, endedAt: null, duration: 0, focusedTime: 0, date });
    }
    const travelSessionId = `tr${now}`;
    const newClock = { ...base, traveling: true, travelStartedAt: now, travelSessionId };
    clockRef.current = newClock;                  // so the task-change effect doesn't double-open
    travelSegmentsRef.current = [];
    prevTravelTaskRef.current = undefined;
    dispatch({ type: 'SET_CLOCK', clock: newClock });
    saveClockState(newClock);
    // Concurrent travel route session. overlay:true => excluded from clocked-time sums; it's an
    // annotation carrying the route + per-task segments, not billable work time.
    saveClockSession({ id: travelSessionId, type: 'travel', overlay: true, projectId: null, startedAt: now, endedAt: null, duration: 0, focusedTime: 0, date, taskSegments: [] });
    // Open the first segment for whatever card is active right now (or null = pure commute). Use the
    // same source the task-change effect uses (timer.taskId) so it doesn't immediately re-segment.
    const activeTask = timer?.taskId || null;
    const task = activeTask ? state.tasks.find(x => x.id === activeTask) : null;
    noteTravelTask(activeTask, task?.projectId ? String(task.projectId) : (base.projectId || null));
    prevTravelTaskRef.current = activeTask;
  }, [state.clockedIn, timer, lock, state.tasks, noteTravelTask]);

  // opts.at backdates the end (the stillness watch found the truck parked since then); opts.drop throws the
  // drive away (never got a fix). shared/commands stopTravel: the drive closes and the clock drops the overlay
  // in one group.
  const stopTravel = useCallback((opts = {}) => {
    const cl = clockRef.current;
    if (!cl?.traveling) return;
    let r;
    try { r = commands.stopTravel(cmdCtx({ clock: { ...cl, active: true } }), { at: opts.at ?? null, travelSegments: [...travelSegmentsRef.current], drop: !!opts.drop }); }
    catch (e) { console.error('stopTravel refused:', e); return; }
    const { active, ...rest } = r.clock;
    clockRef.current = rest;
    travelSegmentsRef.current = [];
    prevTravelTaskRef.current = undefined;
    dispatch({ type: 'SET_CLOCK', clock: rest });
    commitWrites(r.writes).catch((e) => console.error('Travel finalize failed:', e));
  }, [state.clockSessions]);  

  // A drive the fixes stopped coming for (parked: iOS pauses updates; GPS off; phone in a drawer) ends by its
  // clock — checked on open and every minute while traveling (shared/travel.js travelIdleEnd).
  useEffect(() => {
    if (!state.clockedIn?.traveling) return undefined;
    const check = () => {
      const r = travelIdleEnd(clockRef.current, Date.now());
      if (r) stopTravel({ at: r.endAt, drop: !!r.noFix });
    };
    check();
    const h = setInterval(check, 60000);
    return () => clearInterval(h);
  }, [state.clockedIn?.traveling, state.clockedIn?.travelSessionId, stopTravel]);

  // Rehydrate the in-memory segment mirror if the app reloaded mid-travel (the ref is not persisted;
  // without this, the next segment write would clobber segments recorded before the reload).
  useEffect(() => {
    const cl = state.clockedIn;
    if (cl?.traveling && cl.travelSessionId && travelSegmentsRef.current.length === 0) {
      const sess = state.clockSessions.find(s => s.id === cl.travelSessionId);
      if (sess?.taskSegments?.length) travelSegmentsRef.current = [...sess.taskSegments];
    }
  }, [state.clockedIn?.traveling, state.clockedIn?.travelSessionId, state.clockSessions]);

  // Active task changed while traveling → record the segment transition (split the drive per task).
  useEffect(() => {
    const cl = state.clockedIn;
    if (!cl?.traveling || !cl.travelSessionId) { prevTravelTaskRef.current = undefined; return; }
    const activeTask = timer?.taskId || null;
    if (prevTravelTaskRef.current === activeTask) return;
    prevTravelTaskRef.current = activeTask;
    const task = activeTask ? state.tasks.find(x => x.id === activeTask) : null;
    noteTravelTask(activeTask, task?.projectId ? String(task.projectId) : (cl.projectId || null));
  }, [state.clockedIn?.traveling, state.clockedIn?.travelSessionId, timer?.taskId, state.tasks, noteTravelTask]);

  const startCard = useCallback((taskId = null) => {
    // Bank the running task's time before the timer restarts — same task included, so a restart
    // on the same card (leaving the cards view) never drops or double-counts. bankable() decides
    // what counts: on the clock, this task's timer, under the cap. (shared/commands startTask)
    let r;
    if (taskId && state.tasks.some((x) => x.id === taskId)) {
      try { r = commands.startTask(cmdCtx(), { taskId }); } catch (e) { console.error('startCard refused:', e); return; }
    } else {
      if (timer?.taskId) bankCurrent();
      r = { writes: [], timer: makeTimerState({ taskId, startedAt: Date.now() }) };
      saveTimerState(r.timer);
    }
    dispatch({ type: 'SET_TIMER', timer: r.timer }); // optimistic
    if (r.writes.length) commitWrites(r.writes).catch((e) => console.error('startCard failed:', e));
  }, [timer, state.clockedIn, state.tasks, state.clockSessions]);  

  // Complete several tasks at once: the running timer's time goes to the task it was timing
  // (or, with no task on the timer, to the first one) — never once per task.
  const completeMany = useCallback(async (ids) => {
    const list = [...new Set(ids)];
    const owner = timer?.taskId && list.includes(timer.taskId) ? timer.taskId : (timer?.taskId ? null : list[0]);
    for (const id of list) await completeTask(id, id === owner ? undefined : { noTime: true });
  }, [completeTask, timer]);

  const getTodayPending = useCallback(() => {
    const today = localToday();
    const blockOrder = { morning: 0, afternoon: 1, evening: 2 };
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return state.tasks
      .filter(t => t.scheduledDate <= today && t.status === 'todo' && !t.parentId && !t.bucket)
      .sort((a, b) => {
        const aSkipped = a.lastSkippedAt || 0;
        const bSkipped = b.lastSkippedAt || 0;
        if (aSkipped && !bSkipped) return 1;
        if (!aSkipped && bSkipped) return -1;
        if (aSkipped && bSkipped) return aSkipped - bSkipped;
        const aOverdue = a.scheduledDate < today;
        const bOverdue = b.scheduledDate < today;
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        const ba = blockOrder[a.timeBlock] ?? 1;
        const bb = blockOrder[b.timeBlock] ?? 1;
        if (ba !== bb) return ba - bb;
        return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
      });
  }, [state.tasks]);

  const getTodayStats = useCallback(() => {
    const today = localToday();
    const todayTasks = state.tasks.filter(t => t.scheduledDate === today && !t.bucket);
    const completed = todayTasks.filter(t => t.status === 'done');
    const totalTime = todayTasks.reduce((sum, t) => sum + (t.timeSpent || 0), 0);
    const totalPauseTime = todayTasks.reduce((sum, t) => sum + (t.pauseTime || 0), 0);
    // Earnings = tasks completed today (by completedAt), regardless of scheduled date
    const todayStart = new Date(today + 'T00:00:00').getTime();
    const todayEnd = todayStart + 86400000;
    const completedToday = state.tasks.filter(t => t.status === 'done' && t.completedAt >= todayStart && t.completedAt < todayEnd);
    const totalMoney = completedToday.reduce((sum, t) => sum + (t.actualValue || t.moneyEarned || 0), 0);
    const pauseBreakdown = {};
    todayTasks.forEach(t => {
      (t.statusLog || []).forEach(log => {
        if (!pauseBreakdown[log.type]) pauseBreakdown[log.type] = 0;
        pauseBreakdown[log.type] += log.duration || 0;
      });
    });
    return {
      total: todayTasks.length,
      completed: completed.length,
      skipped: todayTasks.filter(t => t.skippedCount > 0).length,
      pending: todayTasks.filter(t => t.status === 'todo').length,
      totalTime, totalPauseTime, pauseBreakdown, totalMoney,
      percentage: todayTasks.length > 0 ? Math.round((completed.length / todayTasks.length) * 100) : 0,
    };
  }, [state.tasks]);

  const getWeekStats = useCallback(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startStr = localDateStr(startOfWeek);
    const weekTasks = state.tasks.filter(t => t.scheduledDate >= startStr);
    return {
      total: weekTasks.length,
      completed: weekTasks.filter(t => t.status === 'done').length,
      totalTime: weekTasks.reduce((sum, t) => sum + (t.timeSpent || 0), 0),
      totalMoney: weekTasks.filter(t => t.status === 'done').reduce((sum, t) => sum + (t.actualValue || t.moneyEarned || 0), 0),
      totalPauseTime: weekTasks.reduce((sum, t) => sum + (t.pauseTime || 0), 0),
    };
  }, [state.tasks]);

  // Stats for an arbitrary [start,end] date range (ISO YYYY-MM-DD, inclusive). Same shape as
  // getTodayStats — unlocks "pull yesterday / a specific period" reporting. Pass projectId to scope.
  const getStatsForRange = useCallback((start, end, projectId = null) => {
    const inRange = (d) => d && d >= start && d <= end;
    let scoped = state.tasks.filter((t) => inRange(t.scheduledDate));
    if (projectId) scoped = scoped.filter((t) => t.projectId === projectId);
    const completed = scoped.filter((t) => t.status === 'done');
    const totalTime = scoped.reduce((s, t) => s + (t.timeSpent || 0), 0);
    const totalPauseTime = scoped.reduce((s, t) => s + (t.pauseTime || 0), 0);
    // Earnings by completion date in range (a task scheduled earlier but finished in-range counts).
    const startMs = new Date(start + 'T00:00:00').getTime();
    const endMs = new Date(end + 'T23:59:59').getTime();
    const earnedTasks = state.tasks.filter((t) => t.status === 'done' && t.completedAt >= startMs && t.completedAt <= endMs && (!projectId || t.projectId === projectId));
    const totalMoney = earnedTasks.reduce((s, t) => s + (t.actualValue || t.moneyEarned || 0), 0);
    const pauseBreakdown = {};
    scoped.forEach((t) => (t.statusLog || []).forEach((log) => {
      const k = log.context || log.type || 'break';
      pauseBreakdown[k] = (pauseBreakdown[k] || 0) + (log.duration || 0);
    }));
    return {
      total: scoped.length,
      completed: completed.length,
      skipped: scoped.filter((t) => t.skippedCount > 0).length,
      pending: scoped.filter((t) => t.status === 'todo').length,
      totalTime, totalPauseTime, pauseBreakdown, totalMoney,
      percentage: scoped.length > 0 ? Math.round((completed.length / scoped.length) * 100) : 0,
    };
  }, [state.tasks]);

  // Per-project time/earnings for a range → array sorted by time desc (for timesheets).
  const getProjectsForRange = useCallback((start, end) => {
    const inRange = (d) => d && d >= start && d <= end;
    const map = {};
    state.tasks.filter((t) => inRange(t.scheduledDate)).forEach((t) => {
      const pid = t.projectId || 'none';
      if (!map[pid]) map[pid] = { projectId: t.projectId || null, time: 0, pause: 0, money: 0, total: 0, done: 0 };
      map[pid].time += t.timeSpent || 0;
      map[pid].pause += t.pauseTime || 0;
      map[pid].money += (t.status === 'done' ? (t.actualValue || t.moneyEarned || 0) : 0);
      map[pid].total += 1;
      if (t.status === 'done') map[pid].done += 1;
    });
    return Object.values(map).sort((a, b) => b.time - a.time);
  }, [state.tasks]);

  const getProjectTodayPending = useCallback((projectId) => {
    return getTodayPending().filter(t => t.projectId === projectId);
  }, [getTodayPending]);

  const getProjectTodayStats = useCallback((projectId) => {
    const today = localToday();
    const pt = state.tasks.filter(t => t.scheduledDate === today && t.projectId === projectId && !t.bucket);
    const completed = pt.filter(t => t.status === 'done');
    const totalTime = pt.reduce((s, t) => s + (t.timeSpent || 0), 0);
    const totalPauseTime = pt.reduce((s, t) => s + (t.pauseTime || 0), 0);
    const totalMoney = pt.reduce((s, t) => s + (t.moneyEarned || 0), 0);
    const pauseBreakdown = {};
    pt.forEach(t => (t.statusLog || []).forEach(log => {
      pauseBreakdown[log.type] = (pauseBreakdown[log.type] || 0) + (log.duration || 0);
    }));
    return {
      total: pt.length, completed: completed.length,
      pending: pt.filter(t => t.status === 'todo').length,
      totalTime, totalPauseTime, pauseBreakdown, totalMoney,
      percentage: pt.length > 0 ? Math.round((completed.length / pt.length) * 100) : 0,
    };
  }, [state.tasks]);

  const getProjectAllTimeStats = useCallback((projectId) => {
    const pt = state.tasks.filter(t => t.projectId === projectId);
    const completed = pt.filter(t => t.status === 'done');
    const totalTime = pt.reduce((s, t) => s + (t.timeSpent || 0), 0);
    const totalPauseTime = pt.reduce((s, t) => s + (t.pauseTime || 0), 0);
    const totalMoney = pt.reduce((s, t) => s + (t.moneyEarned || 0), 0);
    const pauseBreakdown = {};
    pt.forEach(t => (t.statusLog || []).forEach(log => {
      pauseBreakdown[log.type] = (pauseBreakdown[log.type] || 0) + (log.duration || 0);
    }));
    const estimated = pt.filter(t => t.estimatedMinutes && t.timeSpent > 0);
    let estAccuracy = null;
    if (estimated.length > 0) {
      const totalEst = estimated.reduce((s, t) => s + t.estimatedMinutes * 60000, 0);
      const totalActual = estimated.reduce((s, t) => s + t.timeSpent, 0);
      estAccuracy = totalEst > 0 ? Math.round((totalActual / totalEst) * 100) : null;
    }
    return {
      total: pt.length, completed: completed.length,
      pending: pt.filter(t => t.status === 'todo').length,
      totalTime, totalPauseTime, pauseBreakdown, totalMoney, estAccuracy,
      percentage: pt.length > 0 ? Math.round((completed.length / pt.length) * 100) : 0,
    };
  }, [state.tasks]);

  // Tree helpers
  const getChildren = useCallback((parentId) => {
    return state.tasks.filter(t => t.parentId === parentId).sort(bySort);
  }, [state.tasks]);

  const getDescendants = useCallback((parentId) => {
    const result = [];
    const collect = (pid) => {
      const children = state.tasks.filter(t => t.parentId === pid);
      children.forEach(c => { result.push(c); collect(c.id); });
    };
    collect(parentId);
    return result;
  }, [state.tasks]);

  const getRootTasks = useCallback((filterFn) => {
    const roots = state.tasks.filter(t => !t.parentId);
    return filterFn ? roots.filter(filterFn) : roots;
  }, [state.tasks]);

  const deleteTaskTree = useCallback((id) => {
    if (!state.tasks.some((t) => t.id === id)) return;
    const { writes, ids } = commands.deleteTask({ tasks: state.tasks }, { id, tree: true });
    dispatch({ type: 'SET_TASKS', tasks: state.tasks.filter(t => !ids.includes(t.id)) });
    commitWrites(writes).catch((e) => console.error('deleteTaskTree failed:', e));
  }, [state.tasks]);

  // Review: work handed back (Willow's, anyone's) → approve (done) or send back with a note.
  const reviewTask = useCallback(async (kind, id, note = '', by = null) => {
    const fn = { approve: commands.approveReview, sendBack: commands.sendBack, handBack: commands.handBack }[kind];
    const r = fn(cmdCtx({ by }), { id, note, by });
    await commitWrites(r.writes).catch((e) => console.error(`review ${kind} failed:`, e));
  }, [state.tasks, timer, lock, state.clockSessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Void tasks (closed with their project): the time views / Daily Log / task page / Tag page read them; a restore by
  // hand (the task page) brings one back with its void subtasks and parents.
  const voidTasks = state.voidTasks || [];
  const tasksWithVoid = useMemo(() => (voidTasks.length ? [...state.tasks, ...voidTasks] : state.tasks), [state.tasks, voidTasks]);
  const restoreTask = useCallback(async (id) => {
    const { writes } = commands.restoreVoidTask({ tasks: tasksWithVoid, now: Date.now }, { id });
    await commitWrites(writes);
  }, [tasksWithVoid]);

  return (
    <TaskContext.Provider value={{
      tasks: state.tasks, voidTasks, tasksWithVoid, restoreTask, clockSessions: state.clockSessions, allClockSessions: state.allClockSessions, clockedIn: state.clockedIn,
      loaded: state.loaded,
      timerRestored: state.timerRestored,   // meta/timer read once — the launch baseline for the switcher (followPlan)
      // Derived from the single source of truth (meta/timer + meta/lock):
      timer, lock, activeTaskId,
      currentTaskStart, pauseStatus, lockedTaskId, accumulatedPause,
      addTask, addTasks, ensureBucketTasks, updateTask, moveTasksToDepartment, guessDepartment, learnDepartment, addNote, moveTask, dropTask, reorderTasks, setManualOrder, clearManualOrder, deleteTask, deleteTaskTree, completeTask, skipTask, resetTask,
      addTimeEntry, updateTimeEntry, deleteTimeEntry, moveTimeEntry, retargetTimer, setTaskDone, completeMany, reviewTask,
      clockIn, clockOut, switchClockProject, runClockCommand, startQuickTask, startTravel, stopTravel, startCard, pauseTask, resumeTask, lockTask, unlockTask,
      getTodayPending, getTodayStats, getWeekStats, getStatsForRange, getProjectsForRange,
      getProjectTodayPending, getProjectTodayStats, getProjectAllTimeStats,
      getChildren, getDescendants, getRootTasks,
      // A task waiting for its after/proof photo (any completion path); the app shows the prompt.
      pendingAfterPhoto, clearAfterPhoto, pendingCapture, clearCapture, pendingNote, clearNote,
    }}>
      {children}
    </TaskContext.Provider>
  );
}

export function useTasks() {
  const context = useContext(TaskContext);
  if (!context) throw new Error('useTasks must be used within TaskProvider');
  return context;
}
